/**
 * The model-portfolio selection rules of a goal portfolio. Pure — no database,
 * no thrown errors — so the portfolio update, the risk rescore, the platform
 * change and every reader decide the same way.
 *
 * A model portfolio stores two ids:
 * - `calculatedPortfolioId` — the collection portfolio the goal's risk score and
 *   platform match right now (null when nothing matches);
 * - `pickedPortfolioId` — a portfolio chosen by hand, or null for Automatic.
 *
 * The portfolio used is the pick when there is one, otherwise the calculated one.
 */

import type { PortfolioModelDeviation } from "./conditional-sources";

export interface ModelSelectionState {
  calculatedPortfolioId: string | null;
  pickedPortfolioId: string | null;
}

export interface ModelSelectionStateWithType extends ModelSelectionState {
  portfolioType?: string | null;
}

/**
 * The portfolio used: the pick when set, otherwise the calculated one. Null for a
 * portfolio that is not a model portfolio (when the type is given). The SQL twin
 * is `portfolioUsedSql` in the API's goal-portfolio-sql module.
 */
export function portfolioUsedId(state: ModelSelectionStateWithType): string | null {
  if (state.portfolioType != null && state.portfolioType !== "model") return null;
  return state.pickedPortfolioId ?? state.calculatedPortfolioId ?? null;
}

/** A portfolio deviates when a pick exists and differs from the calculated portfolio. */
export function modelPortfolioDeviates(state: ModelSelectionStateWithType): boolean {
  if (state.portfolioType != null && state.portfolioType !== "model") return false;
  return state.pickedPortfolioId != null && state.pickedPortfolioId !== state.calculatedPortfolioId;
}

/**
 * The deviation as the three-state key the widget badge, the PDF fields and
 * the AI read share: `not_model` for a portfolio that is not a model portfolio
 * or uses none (no pick, nothing matches), `yes` when it deviates (a pick that
 * differs from the calculated portfolio, a gap-filling pick included), `no`
 * otherwise (Automatic, or a pick equal to the match).
 */
export function modelPortfolioDeviation(
  state: ModelSelectionStateWithType,
): PortfolioModelDeviation {
  if (state.portfolioType != null && state.portfolioType !== "model") return "not_model";
  if (portfolioUsedId(state) == null) return "not_model";
  return modelPortfolioDeviates(state) ? "yes" : "no";
}

export type PickRequestError = "not_model_type" | "disabled" | "no_risk_score" | "not_eligible";

export type PickRequestOutcome =
  | { patch: { pickedPortfolioId: string | null } | null; error?: never }
  | { error: PickRequestError; patch?: never };

/**
 * Decide a pick request: an id to pin, or null for Automatic.
 * - only a model portfolio takes a pick;
 * - Automatic (null) is always allowed;
 * - re-sending the current pick is a no-op and always allowed (the setting may
 *   have been switched off since);
 * - any other id needs the collection setting on, a risk score on the goal, and
 *   the id among the eligible candidates. The matching portfolio's id is stored
 *   as a pick like any other.
 * `patch` is null when nothing changes.
 */
export function applyPickRequest(input: {
  portfolioType: string | null | undefined;
  selectionAllowed: boolean;
  goalRiskScore: number | null | undefined;
  eligibleIds: readonly string[];
  currentPick: string | null;
  requested: string | null;
}): PickRequestOutcome {
  if (input.portfolioType !== "model") return { error: "not_model_type" };
  if (input.requested === input.currentPick) return { patch: null };
  if (input.requested === null) return { patch: { pickedPortfolioId: null } };
  if (!input.selectionAllowed) return { error: "disabled" };
  if (input.goalRiskScore == null) return { error: "no_risk_score" };
  if (!input.eligibleIds.includes(input.requested)) return { error: "not_eligible" };
  return { patch: { pickedPortfolioId: input.requested } };
}

/** A rescore writes only the calculated portfolio; a pick is never touched. */
export function onRescore(
  state: ModelSelectionState,
  newMatchId: string | null,
): { patch: { calculatedPortfolioId: string | null } | null; usedMoved: boolean } {
  if (state.calculatedPortfolioId === newMatchId) return { patch: null, usedMoved: false };
  const next = { ...state, calculatedPortfolioId: newMatchId };
  return {
    patch: { calculatedPortfolioId: newMatchId },
    usedMoved: portfolioUsedId(next) !== portfolioUsedId(state),
  };
}

/**
 * A platform change recomputes the calculated portfolio and keeps a pick only
 * when the picked portfolio is still an eligible candidate on the new platform.
 */
export function onPlatformChange(
  state: ModelSelectionState,
  newMatchId: string | null,
  pickEligible: boolean,
): { patch: Partial<ModelSelectionState> | null; pickDropped: boolean; usedMoved: boolean } {
  const pickDropped = state.pickedPortfolioId != null && !pickEligible;
  const next: ModelSelectionState = {
    calculatedPortfolioId: newMatchId,
    pickedPortfolioId: pickDropped ? null : state.pickedPortfolioId,
  };
  const patch: Partial<ModelSelectionState> = {};
  if (next.calculatedPortfolioId !== state.calculatedPortfolioId) {
    patch.calculatedPortfolioId = next.calculatedPortfolioId;
  }
  if (pickDropped) patch.pickedPortfolioId = null;
  return {
    patch: Object.keys(patch).length > 0 ? patch : null,
    pickDropped,
    usedMoved: portfolioUsedId(next) !== portfolioUsedId(state),
  };
}

/**
 * A portfolio type change: leaving model clears both ids, entering model starts
 * on Automatic with the current match, and the same type re-sent changes nothing
 * (so the wizard's repeated autosave keeps a pick). Null = no change.
 */
export function onPortfolioTypeChange(
  fromType: string | null | undefined,
  toType: string,
  matchId: string | null,
): ModelSelectionState | null {
  if (fromType === toType) return null;
  if (toType === "model") return { calculatedPortfolioId: matchId, pickedPortfolioId: null };
  return { calculatedPortfolioId: null, pickedPortfolioId: null };
}
